import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SendOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row, UploadFile } from 'antd';
import { TextAreaRef } from 'antd/es/input/TextArea';

import { isArray, isEmpty } from 'lodash';

import { SmsDirection, SmsServiceType } from '../../../../shared/constants/enums';
import { getImageUrlFromBytes } from '../../../../shared/utils';
import { AppDispatch, RootState } from '../../../../store';
import { AttachmentFile, ChatsCollection, MakeClearData, MessagesCollection, SmsChat, SMSMediaResource } from '../../models';
import * as actions from '../../store/actions';
import * as smsConstants from '../../store/constants/sms-constants';
import UploadPhotos from '../../upload-files';

import './styles.less';

export type MessageTextAreaProps = {
  makeClearData: MakeClearData;
  disabled: boolean;
};

const MessageTextArea = ({ makeClearData, disabled }: MessageTextAreaProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const messageRef = useRef<TextAreaRef | null>(null);

  const [textMessage, setTextMessage] = useState<string | undefined>();
  const [attachedPhotos, setAttachedPhotos] = useState<AttachmentFile[] | undefined>();
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const [sendButtonDisabled, setSendButtonDisabled] = useState<boolean>(false);

  const { sendMessageResultLoading, messageControlParameters, messageControlParametersLoading, localMessages, selectedChatItem } = useSelector(
    (state: RootState) => state.smses,
  );
  const { localChats } = useSelector((state: RootState) => state.smsChat);

  useEffect(() => {
    setSendButtonDisabled(
      !selectedChatItem || !selectedChatItem.phoneNumber || (isEmpty(textMessage?.trim()) && isEmpty(attachedPhotos)) || disabled,
    );
  }, [selectedChatItem, textMessage, attachedPhotos]);

  useEffect(() => {
    clearStateData();
  }, [makeClearData]);

  const clearStateData = () => {
    setTextMessage(undefined);
    setAttachedPhotos([]);
    setFileList([]);

    if (selectedChatItem) {
      if (messageRef?.current && !disabled) {
        messageRef.current.focus();
      }
    }
  };

  const onMessageChange = (messageValue: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextMessage(messageValue.target.value);
  };

  const onSendClick = () => {
    if (disabled || !messageControlParameters || !selectedChatItem) {
      return;
    }

    dispatch(
      actions.sendMessage({
        smsServiceType: messageControlParameters.smsServiceType,
        toPhoneNumber: selectedChatItem.phoneNumber,
        myNumber: messageControlParameters.myNumber,
        toPhoneNumberName: selectedChatItem.chatName,
        textMessage: textMessage?.trim(),
        attachedPhotos,
        smsChatId: selectedChatItem.smsChatId,
        truckingCompanyId: selectedChatItem.truckingCompanyId,
        phoneGroupSmsServiceType: messageControlParameters.phoneGroupSmsServiceType,
        phoneGroupId: messageControlParameters.phoneGroupId,
        putSmsTemplate: true,
        createNote: true,
      }),
    ).then((result) => {
      let newSelectedChatItem = { ...selectedChatItem } as SmsChat;

      if (isEmpty(selectedChatItem?.smsChatId)) {
        const tempChatItems = localChats?.chatList ?? [];

        dispatch(
          actions.setLocalChats({
            chatList: [
              {
                phoneNumber: result.toNumber,
                chatName: result.toName,
                unreadCount: 0,
                changeDate: result.changeDate,
                smsChatId: result.smsChatId,
                truckingCompanyId: result.truckingCompanyId,
              } as SmsChat,
              ...tempChatItems,
            ],
            hasMore: localChats?.hasMore ?? false,
            lastChangeDateTime: result.changeDate,
          }),
        );

        newSelectedChatItem.phoneNumber = result.toNumber;
        newSelectedChatItem.chatName = result.toName;
        newSelectedChatItem.unreadCount = 0;
        newSelectedChatItem.changeDate = result.changeDate;
        newSelectedChatItem.smsChatId = result.smsChatId;
        newSelectedChatItem.truckingCompanyId = result.truckingCompanyId;

        dispatch(actions.setSelectedChatItem(newSelectedChatItem));
      } else {
        const newChatItems = { ...localChats } as ChatsCollection;
        const chatListIndex = newChatItems.chatList?.findIndex((x) => x.smsChatId === result.smsChatId) ?? -1;
        const chatItem = { ...newChatItems.chatList?.[chatListIndex] } as SmsChat;

        if (chatItem && newChatItems.chatList) {
          chatItem.changeDate = result.changeDate;
          chatItem.truckingCompanyId = result.truckingCompanyId;
          chatItem.chatName = result.toName;

          const newChatList = newChatItems.chatList.filter((x) => x.smsChatId !== result.smsChatId);
          newChatItems.chatList = [chatItem, ...newChatList];
          newChatItems.lastChangeDateTime = result.changeDate;

          dispatch(actions.setLocalChats(newChatItems));
        }

        newSelectedChatItem.changeDate = result.changeDate;

        if (newSelectedChatItem.truckingCompanyId !== result.truckingCompanyId) {
          newSelectedChatItem.truckingCompanyId = result.truckingCompanyId;
          newSelectedChatItem.chatName = result.toName;
        }

        dispatch(actions.setSelectedChatItem(newSelectedChatItem));
      }

      const tempArrayMessages = localMessages?.messageList ?? [];

      const mediaResourceList: SMSMediaResource[] = [];

      if (isArray(attachedPhotos) && !isEmpty(attachedPhotos)) {
        attachedPhotos.forEach((x) => {
          mediaResourceList.push({
            url: getImageUrlFromBytes(new Uint8Array(x.fileBytes)),
          } as SMSMediaResource);
        });
      }

      dispatch(
        actions.setLocalMessages({
          messageList: [
            ...tempArrayMessages,
            {
              id: result.id,
              subject: result.subject,
              direction: SmsDirection.Outbound,
              changeDate: result.changeDate,
              createDate: result.createDate,
              isRead: true,
              smsChatId: result.smsChatId,
              messageStatus: result.messageStatus,
              mediaResourceList: mediaResourceList,
              fromNumber: result.fromNumber,
            },
          ],
          hasMore: localMessages?.hasMore ?? false,
          newUnreadCount: localMessages?.newUnreadCount ?? 0,
        } as MessagesCollection),
      );

      dispatch(actions.setSelectedChatItem(newSelectedChatItem));

      clearStateData();
    });
  };

  const onPressEnterTextArea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && !disabled && selectedChatItem?.phoneNumber && (!isEmpty(textMessage?.trim()) || !isEmpty(attachedPhotos))) {
      e.preventDefault();
      onSendClick();
    }
  };

  return (
    <Row className="send-text-area-part">
      <Col span={24}>
        <Input.TextArea
          rows={3}
          value={textMessage}
          onChange={onMessageChange}
          className="send-text-area"
          variant="borderless"
          maxLength={
            messageControlParameters &&
            (messageControlParameters.smsServiceType === SmsServiceType.OwnRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.AgencyOwnRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.DedicatedRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.DefaultRingCentral)
              ? smsConstants.MAX_MESSAGE_RING_CENTRAL_LENGTH
              : smsConstants.MAX_MESSAGE_LENGTH
          }
          disabled={!selectedChatItem || !selectedChatItem.phoneNumber || sendMessageResultLoading || disabled}
          onPressEnter={onPressEnterTextArea}
          showCount={true}
          ref={messageRef}
        />

        <Button
          className="send-message-button"
          icon={<SendOutlined />}
          type="primary"
          shape="circle"
          onClick={onSendClick}
          title="Send message"
          disabled={sendButtonDisabled}
          loading={sendMessageResultLoading}
        />

        {messageControlParameters && !messageControlParametersLoading ? (
          <UploadPhotos
            fileList={fileList}
            setAttachedPhotos={setAttachedPhotos}
            setFileList={setFileList}
            disabled={!selectedChatItem || !selectedChatItem.phoneNumber || disabled}
          />
        ) : undefined}
      </Col>
    </Row>
  );
};

export default MessageTextArea;
